'use client'

import Script from 'next/script'
import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useState } from 'react'

import type { Sierra } from '@src/components/Sierra/types'
import { configPublic } from '@src/config.public'
import { useLockScroll } from '@src/hooks/useLockScroll'
import { useLocale } from '@src/i18n/LocaleProvider'
import { trackOpenChat, trackStartConversation } from '@src/utils/analytics.util'

import type { SierraConfig } from './types'

interface SierraContext {
  sierra?: Sierra
}

interface SierraProviderProps {
  children: ReactNode
}

const SierraContext = createContext<SierraContext>({})

export const SierraProvider = (props: SierraProviderProps) => {
  const { children } = props
  const { locale } = useLocale()
  const [sierra, setSierra] = useState<Sierra>()
  const [isOpen, setIsOpen] = useState(false)
  useLockScroll({ isExpanded: isOpen })

  const isSierraEnabled = configPublic.sierra.isEnabled && locale.code === 'en-US'

  useEffect(() => {
    window.sierraConfig = {
      customGreeting:
        'Hi, I’m Adina, your personal BODi virtual assistant. I can help you find your perfect fitness program. Just let me know what you’re looking for— feel free to include things like your fitness goals, experience level, or favorite workout style so I can make the best recommendation.',
      persistence: 'tab',

      colors: {
        background: '#ffffff',
        text: '#161819',
        border: '#A09EA3',
        titleBar: '#161819',
        titleBarText: '#ffffff',
        assistantBubble: '#EDFAFF',
        assistantBubbleText: '#164646',
        userBubble: '#d8dbdf',
        userBubbleText: '#4a4e5a',
      },
      display: 'corner',
      hideOnClose: true,

      onOpen(opts) {
        trackOpenChat(opts.isNewConversation)
        setIsOpen(true)
      },
      onClose() {
        setIsOpen(false)
      },
      onConversationStart(opts) {
        trackStartConversation(opts.conversationID)
      },
    } as SierraConfig
  }, [])

  return (
    <SierraContext.Provider value={{ sierra }}>
      <>
        {isSierraEnabled && (
          <Script
            type="module"
            strategy="afterInteractive"
            crossOrigin="anonymous"
            src={configPublic.sierra.scriptUrl}
            onLoad={() => {
              setSierra(window?.sierra)
            }}
          />
        )}
        {children}
      </>
    </SierraContext.Provider>
  )
}

export const useSierra = () => {
  const context = useContext(SierraContext)
  if (!context) {
    throw new Error('useSierra must be used within SierraContext')
  }

  return context
}
